import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatPts, formatBattleName, isLive } from '../utils';
import CountdownTimer from '../components/CountdownTimer';
import Icon from '../components/Icon';

const NEIGHBORHOOD = 4;

export default function Home({ refreshKey }) {
  const [data, setData] = useState(null);
  const [clanIcons, setClanIcons] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setClanIcons({});
    api.dashboard()
      .then(d => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
        if (d?.clans?.length && d?.battle?.clanBattleName) {
          const selected = d.clans.find(c => c.clanName.toLowerCase() === d.myClan.toLowerCase());
          const visibleClans = selected
            ? d.clans.filter(c => Math.abs(c.rank - selected.rank) <= NEIGHBORHOOD)
            : d.clans.slice(0, 9);

          Promise.allSettled(
            visibleClans.map(c =>
              api.clanInfo(c.clanName, d.battle.clanBattleName)
                .then(info => [c.clanName.toLowerCase(), info?.icon || null])
            )
          ).then(results => {
            if (cancelled) return;
            const icons = {};
            for (const result of results) {
              if (result.status === 'fulfilled' && result.value[1]) {
                icons[result.value[0]] = result.value[1];
              }
            }
            setClanIcons(icons);
          });
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) return <div className="page-center"><div className="spinner" /></div>;
  if (error) return <div className="page"><div className="error-box">Failed to load: {error}</div></div>;
  if (!data?.battle) return <div className="page"><div className="empty-msg">No battles found.</div></div>;

  const { battle, myClan, clans } = data;
  const live = isLive(battle);
  const name = formatBattleName(battle.clanBattleName);
  const me = clans.find(c => c.clanName.toLowerCase() === myClan.toLowerCase()) || null;
  const ahead = me ? clans.find(c => c.rank === me.rank - 1) : null;
  const rows = me ? clans.filter(c => Math.abs(c.rank - me.rank) <= NEIGHBORHOOD) : clans.slice(0, 9);
  const maxPoints = Math.max(...rows.map(c => Number(c.points)), 1);
  const myClanIcon = clanIcons[myClan.toLowerCase()];
  const catchUpHours = me && ahead && me.pph1h > ahead.pph1h
    ? (ahead.points - me.points) / (me.pph1h - ahead.pph1h)
    : null;

  const goToClan = c => {
    if (c.clanId) navigate(`/clan-stats?clanId=${c.clanId}&battleId=${battle.id}`);
  };

  return (
    <div className="page command-page fade-in">
      <header className="command-header">
        <div>
          <div className="eyebrow">Command center</div>
          <h1>Good evening, commander.</h1>
          <p>Here is what is moving in the current clan battle.</p>
        </div>
        <button className="quiet-action" onClick={() => navigate('/search')}>
          <Icon name="search" size={17} /> Find player <kbd>/</kbd>
        </button>
      </header>

      <section className="battle-spotlight">
        <div className="spotlight-main">
          <div className="spotlight-status">
            <span className={`live-signal${live ? '' : ' ended'}`} />
            {live ? 'Live battle' : 'Latest battle'}
          </div>
          <h2>{name}</h2>
          <div className="spotlight-actions">
            <button className="btn btn-primary" onClick={() => navigate(`/battle/${battle.id}`)}>
              Open battle room <Icon name="arrow" size={16} />
            </button>
            <button className="btn btn-quiet" onClick={() => navigate('/battles')}>View archive</button>
          </div>
        </div>

        <div className="spotlight-clock">
          <span className="spotlight-clock-label">{live ? 'Battle closes in' : 'Battle status'}</span>
          <div className="spotlight-clock-value">
            {live ? <CountdownTimer finishesAt={battle.finishesAt} /> : 'Finished'}
          </div>
          <span className="spotlight-clock-note">{live ? 'Live telemetry is active' : 'Final standings recorded'}</span>
        </div>

        {me && (
          <div className="our-position">
            <div className="our-position-top">
              {myClanIcon
                ? <img className="clan-mark clan-mark-image" src={myClanIcon} alt={`${myClan} emblem`} />
                : <div className="clan-mark">{myClan[0]?.toUpperCase()}</div>}
              <div><span>Your clan</span><strong>{myClan}</strong></div>
              <div className="position-rank"><span>Position</span><strong>#{me.rank}</strong></div>
            </div>
            <div className="position-track"><i style={{ width: `${Math.max(8, 100 - (me.rank - 1) * 2)}%` }} /></div>
            <div className="position-stats">
              <div><span>Points</span><strong>{formatPts(me.points)}</strong></div>
              <div><span>Current pace</span><strong className="positive">+{formatPts(me.pph1h || 0)}/h</strong></div>
              <div><span>Next target</span><strong>{ahead ? formatPts(ahead.points - me.points) : 'Leading'}</strong></div>
            </div>
          </div>
        )}
      </section>

      <section className="signal-strip">
        <div className="signal-card">
          <Icon name="activity" />
          <div><span>1 hour velocity</span><strong>{me?.pph1h != null ? `+${formatPts(me.pph1h)}` : '—'}</strong></div>
          <small>points / hour</small>
        </div>
        <div className="signal-card">
          <Icon name="shield" />
          <div><span>Gap to next rank</span><strong>{ahead && me ? formatPts(ahead.points - me.points) : 'Front runner'}</strong></div>
          <small>{ahead ? ahead.clanName : 'Hold the lead'}</small>
        </div>
        <div className="signal-card">
          <Icon name="clock" />
          <div><span>Catch-up estimate</span><strong>{catchUpHours != null && catchUpHours < 48 ? `${catchUpHours.toFixed(1)}h` : 'No estimate'}</strong></div>
          <small>{catchUpHours != null ? 'at current pace' : 'pace must increase'}</small>
        </div>
      </section>

      <section className="race-section">
        <div className="section-heading">
          <div>
            <div className="eyebrow">{me ? 'Your race window' : 'Leading clans'}</div>
            <h2>{me ? 'Clans around your position' : 'Current leaderboard'}</h2>
          </div>
          <button className="text-action" onClick={() => navigate('/top50')}>Full global ranking <Icon name="arrow" size={15} /></button>
        </div>

        <div className="race-board">
          <div className="race-labels"><span>Rank & clan</span><span>Battle points</span><span>1h pace</span><span>Gap</span></div>
          {rows.map(c => {
            const isMe = me && c.rank === me.rank;
            const gap = me ? c.points - me.points : null;
            const icon = clanIcons[c.clanName.toLowerCase()];
            return (
              <button key={c.clanName} className={`race-row${isMe ? ' is-us' : ''}`} onClick={() => goToClan(c)}>
                <span className="race-identity">
                  <b className="race-rank">{String(c.rank).padStart(2, '0')}</b>
                  {icon
                    ? <img className="mini-clan-mark clan-mark-image" src={icon} alt={`${c.clanName} emblem`} />
                    : <i className="mini-clan-mark">{c.clanName[0]?.toUpperCase()}</i>}
                  <span><strong>{c.clanName}</strong>{isMe && <small>Your clan</small>}</span>
                </span>
                <span className="race-points">
                  <i><b style={{ width: `${Number(c.points) / maxPoints * 100}%` }} /></i>
                  <strong>{formatPts(c.points)}</strong>
                </span>
                <span className="race-pace">{c.pph1h != null ? `+${formatPts(c.pph1h)}` : '—'}<small>/h</small></span>
                <span className={`race-gap${gap < 0 ? ' safe' : ''}`}>
                  {!me || isMe ? '—' : gap > 0 ? `+${formatPts(gap)}` : formatPts(gap)}
                </span>
                <Icon name="arrow" size={16} className="race-arrow" />
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
